import { useEffect, useState, useRef } from 'react';
import { Video, Pin } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2O PR 2 — Pinned Clips strip.
 *
 * Renders inside the Squad Detail "Feed" tab as a horizontally-scrollable
 * highlight reel of the squad's pinned clips (memory anchors).
 */
export default function ArenaxSquadPinnedClips({ squadId }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!squadId) return;
    api.get(`/arenax/squads/${squadId}/clips/pinned?limit=12`)
      .then(r => setClips(r.data?.clips || []))
      .catch(() => setClips([]))
      .finally(() => setLoading(false));
  }, [squadId]);

  if (loading || clips.length === 0) return null;

  return (
    <section className="rounded-2xl border border-amber-500/25 overflow-hidden"
      data-testid="arenax-squad-pinned-clips"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.06), var(--ax-card-bg-soft))' }}>
      <div className="px-5 pt-5 pb-3 border-b border-amber-500/15">
        <div className="flex items-center gap-2">
          <Pin className="h-4 w-4 text-amber-300" />
          <span className="text-[10px] tracking-[0.3em] text-amber-300 font-black uppercase">Pinned Highlights</span>
          <span className="text-[10px] text-slate-500">{clips.length}</span>
        </div>
        <div className="mt-2 text-sm text-slate-400">The crew's memory anchors.</div>
      </div>
      <div className="px-4 py-4 overflow-x-auto">
        <div className="flex gap-3 min-w-min">
          {clips.map(clip => (
            <PinnedClipTile key={clip.id} clip={clip} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PinnedClipTile({ clip }) {
  const videoRef = useRef(null);
  const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/arenax/clips/${clip.id}/stream`;
  const onEnter = () => { try { videoRef.current?.play(); } catch (_e) { /* no-op */ } };
  const onLeave = () => { try { videoRef.current?.pause(); videoRef.current.currentTime = 0; } catch (_e) { /* no-op */ } };
  return (
    <div className="shrink-0 w-36 rounded-xl overflow-hidden border border-amber-500/30 ring-1 ring-amber-500/20 bg-slate-950"
      data-testid={`arenax-pinned-clip-${clip.id}`}
      onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="relative aspect-[9/16] bg-slate-900">
        <video ref={videoRef} src={streamUrl} preload="metadata" muted playsInline loop
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-amber-500/95 text-slate-950
                          text-[8px] font-black uppercase tracking-wider flex items-center gap-1">
          <Pin className="h-2 w-2" />
        </div>
      </div>
      <div className="px-2 py-1.5">
        <p className="text-[10.5px] text-slate-200 leading-snug line-clamp-3">{clip.caption_narrative}</p>
      </div>
    </div>
  );
}
